//--- ARQUIVO ATUALIZADO: AccountsView.js (Etapa 4 - Exibindo Saldo) ---
import React, { useState, useMemo } from 'react';
import { EditIcon, TrashIcon } from './Icons.js';
// Importa as funções de utils.js
import { formatCurrencyDisplay, formatDateDisplay, calculateAccountBalance } from './utils.js';

// --- REMOVIDO: formatCurrencyDisplay daqui, pois agora vem de utils.js ---
// Helper to format currency input (removes formatting, converts to number)
const parseCurrencyInput = (value) => {
    const numericString = String(value).replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(numericString) || 0;
};
// Helper to format currency input mask
const formatCurrencyInputMask = (value) => {
    const numericValue = String(value).replace(/[^\d]/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(number);
};

// --- Componente do Formulário de Conta (sem alterações) ---
const AccountForm = ({ onSave, onCancel, account, showNotification }) => {
    const [formData, setFormData] = useState({
        name: account ? account.name : '',
        startingBalanceInput: account ? formatCurrencyInputMask(account.startingBalance * 100) : '',
        openingDate: account ? account.openingDate : new Date().toISOString().split('T')[0]
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'startingBalanceInput') {
            setFormData(prev => ({ ...prev, [name]: formatCurrencyInputMask(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const startingBalance = parseCurrencyInput(formData.startingBalanceInput);
        if (!formData.name.trim() || !formData.openingDate) {
            showNotification("Nome da Conta e Data de Abertura são obrigatórios.");
            return;
        }
        setIsSaving(true);
        try {
            await onSave({
                 ...account,
                 name: formData.name.trim(),
                 startingBalance: startingBalance,
                 openingDate: formData.openingDate
             });
        } finally {
            setIsSaving(false);
        }
    };
    const inputClasses = "mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500";
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                <form onSubmit={handleSubmit}>
                    {/* ... (conteúdo do formulário permanece o mesmo) ... */}
                     <div className="p-6">
                        <h3 className="text-xl font-bold dark:text-gray-200 mb-6">{account ? 'Editar Conta' : 'Nova Conta Financeira'}</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="acc-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome da Conta *</label>
                                <input id="acc-name" name="name" type="text" value={formData.name} onChange={handleChange} required placeholder="Ex: Caixa, Conta Nubank" className={inputClasses} />
                            </div>
                            <div>
                                <label htmlFor="acc-balance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Saldo Inicial (R$) *</label>
                                <input id="acc-balance" name="startingBalanceInput" type="text" inputMode="decimal" value={formData.startingBalanceInput} onChange={handleChange} required placeholder="1.250,75" className={`${inputClasses} text-right`} />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">O saldo que a conta tinha na Data de Abertura abaixo.</p>
                            </div>
                            <div>
                                <label htmlFor="acc-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Data de Abertura (para Saldo Inicial) *</label>
                                <input id="acc-date" name="openingDate" type="date" value={formData.openingDate} onChange={handleChange} required className={inputClasses} />
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex justify-end space-x-4 rounded-b-lg">
                        <button type="button" onClick={onCancel} disabled={isSaving} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400">{isSaving ? 'Salvando...' : 'Salvar Conta'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Componente Principal da Tela de Contas ---
// Adiciona 'transactions' como prop
export const AccountsView = ({ accounts, transactions, onAdd, onEdit, onDelete, showNotification }) => {
    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);

    // --- REMOVIDO: formatDateDisplay daqui, pois agora vem de utils.js ---

    // Calcula os saldos atuais para todas as contas usando useMemo para otimização
    const accountBalances = useMemo(() => {
        const balances = {};
        accounts.forEach(acc => {
            // Chama a função importada para calcular o saldo
            balances[acc.id] = calculateAccountBalance(acc.id, accounts, transactions);
        });
        return balances;
    }, [accounts, transactions]); // Recalcula se contas ou transações mudarem


    const handleSave = async (accountData) => {
        if (accountData.id) { await onEdit(accountData); }
        else { await onAdd(accountData); }
        setShowForm(false);
        setEditingAccount(null);
    };

    const openFormForNew = () => { setEditingAccount(null); setShowForm(true); };
    const openFormForEdit = (account) => { setEditingAccount(account); setShowForm(true); };
    const handleDeleteClick = (accountId) => { onDelete(accountId); };

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Contas Financeiras</h2>
                <button onClick={openFormForNew} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">+ Nova Conta</button>
            </div>

            {/* --- Lista de Contas Atualizada --- */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                <th className="px-6 py-3">Nome da Conta</th>
                                <th className="px-6 py-3 text-right">Saldo Inicial</th>
                                <th className="px-6 py-3">Data Saldo Inicial</th>
                                <th className="px-6 py-3 text-right">Saldo Atual</th> {/* <-- NOVA COLUNA */}
                                <th className="px-6 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {accounts.length > 0 ? accounts.map(account => {
                                // Pega o saldo calculado do objeto 'accountBalances'
                                const currentBalance = accountBalances[account.id];
                                const isBalanceNegative = currentBalance !== null && currentBalance < 0;

                                return (
                                <tr key={account.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-gray-900 dark:text-gray-200">{account.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="text-sm text-gray-900 dark:text-gray-300">{formatCurrencyDisplay(account.startingBalance)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500 dark:text-gray-400">{formatDateDisplay(account.openingDate)}</div>
                                    </td>
                                     {/* --- Célula do Saldo Atual --- */}
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className={`text-sm font-semibold ${isBalanceNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-200'}`}>
                                            {currentBalance !== null ? formatCurrencyDisplay(currentBalance) : 'Erro'}
                                        </div>
                                    </td>
                                     {/* --- Fim da Célula --- */}
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => openFormForEdit(account)} className="text-indigo-600 hover:text-indigo-900 mr-4" title="Editar Conta"><EditIcon /></button>
                                        <button onClick={() => handleDeleteClick(account.id)} className="text-red-600 hover:text-red-900" title="Excluir Conta"><TrashIcon /></button>
                                    </td>
                                </tr>
                                )
                            }) : (
                                <tr><td colSpan="5" className="text-center py-10 text-gray-500 dark:text-gray-400">Nenhuma conta cadastrada.</td></tr> // Colspan atualizado para 5
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- Modal do Formulário --- */}
            {showForm && (
                <AccountForm
                    onSave={handleSave}
                    onCancel={() => { setShowForm(false); setEditingAccount(null); }}
                    account={editingAccount}
                    showNotification={showNotification}
                />
            )}
        </>
    );
};